import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next'; // Ավելացված է
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faTrash, faPlus, faEdit, faSave, faTimes, 
    faQuestionCircle, faListUl, faClipboardList, faCheckDouble 
} from '@fortawesome/free-solid-svg-icons';
import { API_BASE } from '../apiConfig';

const AdminFAQSection = ({ list, refresh }) => {
    const { t } = useTranslation(); // Ավելացված է
    const [formData, setFormData] = useState({ question: '', answer: '' });
    const [editId, setEditId] = useState(null);
    const [loading, setLoading] = useState(false);

    const getOptions = useCallback((method = 'GET', body = null) => {
        const token = localStorage.getItem('token');
        const options = {
            method: method,
            headers: { 
                'Content-Type': 'application/json',
                'ngrok-skip-browser-warning': 'true',
                'Authorization': `Bearer ${token}`
            }
        };
        if (body) options.body = JSON.stringify(body);
        return options;
    }, []);

    const handleSave = async (e) => {
        e.preventDefault();
        setLoading(true);
        const url = editId ? `${API_BASE}/api/faq/${editId}` : `${API_BASE}/api/faq`;
        const method = editId ? 'PUT' : 'POST';

        try {
            const res = await fetch(url, getOptions(method, formData));

            if (res.ok) {
                setFormData({ question: '', answer: '' });
                setEditId(null);
                refresh();
            } else if (res.status === 401 || res.status === 403) {
                alert(t('session_expired_error'));
            }
        } catch (err) {
            console.error("FAQ save error:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (item) => {
        setEditId(item.id);
        setFormData({ question: item.question, answer: item.answer });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('delete_faq_confirm'))) return;
        try {
            const res = await fetch(`${API_BASE}/api/faq/${id}`, getOptions('DELETE'));
            if (res.ok) {
                refresh();
            }
        } catch (e) { 
            console.error(e); 
        }
    };

    return (
        <div style={styles.container}>
            
            {/* --- FAQ ANALYTICS --- */}
            <div style={styles.analyticsRow}>
                <div style={{...styles.anaCard, background: 'linear-gradient(135deg, #4318FF 0%, #5E37FF 100%)'}}>
                    <div style={styles.anaIconBox}><FontAwesomeIcon icon={faClipboardList} /></div>
                    <div>
                        <p style={styles.anaLabel}>{t('faq_count_label')}</p>
                        <h3 style={styles.anaVal}>{list.length}</h3>
                    </div>
                </div>
                <div style={{...styles.anaCard, background: 'linear-gradient(135deg, #01B574 0%, #2AF598 100%)'}}>
                    <div style={styles.anaIconBox}><FontAwesomeIcon icon={faCheckDouble} /></div>
                    <div>
                        <p style={styles.anaLabel}>{t('faq_status_label')}</p>
                        <h3 style={styles.anaVal}>{t('faq_status_published')}</h3>
                    </div>
                </div>
            </div>

            {/* --- FORM CARD --- */}
            <div style={styles.card}>
                <div style={styles.cardHeader}>
                    <div style={styles.iconBox}>
                        <FontAwesomeIcon icon={editId ? faEdit : faPlus} />
                    </div>
                    <h3 style={styles.cardTitle}>
                        {editId ? t('edit_faq_title') : t('add_faq_title')}
                    </h3>
                </div>

                <form onSubmit={handleSave} style={styles.form}>
                    <div style={styles.inputGroup}>
                        <label style={styles.label}>{t('faq_question_label')}</label>
                        <input 
                            style={styles.input}
                            placeholder={t('faq_question_placeholder')} 
                            value={formData.question} 
                            onChange={e => setFormData({...formData, question: e.target.value})} 
                            required 
                        />
                    </div>
                    
                    <div style={styles.inputGroup}>
                        <label style={styles.label}>{t('faq_answer_label')}</label>
                        <textarea 
                            style={styles.textarea}
                            placeholder={t('faq_answer_placeholder')} 
                            value={formData.answer} 
                            onChange={e => setFormData({...formData, answer: e.target.value})} 
                            required 
                            rows="4"
                        />
                    </div>

                    <div style={styles.btnRow}>
                        <button type="submit" style={styles.saveBtn} disabled={loading}>
                            <FontAwesomeIcon icon={faSave} style={{marginRight: '8px'}} />
                            {editId ? t('btn_update') : t('btn_save')}
                        </button>
                        {editId && (
                            <button type="button" onClick={() => {setEditId(null); setFormData({question:'', answer:''})}} style={styles.cancelBtn}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        )}
                    </div>
                </form>
            </div>

            {/* --- LIST TABLE CARD --- */}
            <div style={{...styles.card, marginTop: '30px'}}>
                <div style={styles.cardHeader}>
                    <div style={{...styles.iconBox, background: '#F4F7FE', color: '#4318FF'}}>
                        <FontAwesomeIcon icon={faListUl} />
                    </div>
                    <h3 style={styles.cardTitle}>{t('existing_faqs_title')}</h3>
                </div>

                <div style={styles.tableWrapper}>
                    <table style={styles.table}>
                        <thead>
                            <tr style={styles.thRow}>
                                <th style={styles.th}>{t('table_question')}</th>
                                <th style={styles.th}>{t('table_answer')}</th>
                                <th style={{...styles.th, textAlign: 'center'}}>{t('table_action')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {list.map(item => (
                                <tr key={item.id} style={styles.tr}>
                                    <td style={styles.tdQuestion}>
                                        <FontAwesomeIcon icon={faQuestionCircle} style={styles.qIcon} />
                                        {item.question}
                                    </td>
                                    <td style={styles.tdAnswer}>
                                        <div style={styles.answerScroll}>{item.answer}</div>
                                    </td>
                                    <td style={styles.tdActions}>
                                        <button onClick={() => handleEdit(item)} style={styles.editBtn}>
                                            <FontAwesomeIcon icon={faEdit} />
                                        </button>
                                        <button onClick={() => handleDelete(item.id)} style={styles.deleteBtn}>
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};
// ... styles unchanged
const styles = {
    container: { fontFamily: "'Plus Jakarta Sans', sans-serif" },
    analyticsRow: { display: 'flex', gap: '20px', marginBottom: '30px' },
    anaCard: { flex: 1, padding: '20px', borderRadius: '20px', color: '#fff', display: 'flex', alignItems: 'center', gap: '15px', boxShadow: '0 10px 20px rgba(0,0,0,0.05)' },
    anaIconBox: { width: '45px', height: '45px', borderRadius: '12px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' },
    anaLabel: { fontSize: '11px', margin: 0, opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.5px' },
    anaVal: { fontSize: '22px', margin: '2px 0 0 0', fontWeight: '800' },
    card: { background: '#fff', borderRadius: '24px', padding: '25px', boxShadow: '0 10px 30px rgba(0,0,0,0.02)', border: '1px solid #f1f5f9' },
    cardHeader: { display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '25px' },
    iconBox: { width: '40px', height: '40px', borderRadius: '12px', background: 'linear-gradient(135deg, #4318FF 0%, #5E37FF 100%)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' },
    cardTitle: { fontSize: '18px', fontWeight: '800', color: '#1B2559', margin: 0 },
    form: { display: 'flex', flexDirection: 'column', gap: '20px' },
    inputGroup: { display: 'flex', flexDirection: 'column', gap: '8px' },
    label: { fontSize: '12px', fontWeight: '700', color: '#1B2559', marginLeft: '5px' },
    input: { padding: '12px 16px', borderRadius: '14px', border: '1px solid #E0E5F2', background: '#F4F7FE', outline: 'none', fontSize: '14px' },
    textarea: { padding: '12px 16px', borderRadius: '14px', border: '1px solid #E0E5F2', background: '#F4F7FE', outline: 'none', fontSize: '14px', resize: 'vertical', fontFamily: 'inherit' },
    btnRow: { display: 'flex', gap: '10px' },
    saveBtn: { flex: 1, background: 'linear-gradient(135deg, #4318FF 0%, #5E37FF 100%)', color: '#fff', border: 'none', padding: '12px', borderRadius: '14px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 15px rgba(67, 24, 255, 0.2)' },
    cancelBtn: { width: '50px', background: '#F4F7FE', color: '#1B2559', border: 'none', borderRadius: '14px', cursor: 'pointer', fontSize: '18px' },
    tableWrapper: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse' },
    thRow: { borderBottom: '1px solid #F4F7FE' },
    th: { padding: '12px', textAlign: 'left', color: '#A3AED0', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase' },
    tr: { borderBottom: '1px solid #F4F7FE' },
    tdQuestion: { padding: '15px 12px', fontSize: '14px', fontWeight: '700', color: '#1B2559', width: '35%' },
    qIcon: { marginRight: '10px', color: '#4318FF', fontSize: '12px' },
    tdAnswer: { padding: '15px 12px', fontSize: '14px', color: '#475569', lineHeight: '1.5' },
    answerScroll: { maxHeight: '60px', overflowY: 'auto' },
    tdActions: { padding: '15px 12px', display: 'flex', justifyContent: 'center', gap: '10px' },
    editBtn: { background: '#E0E7FF', color: '#4318FF', border: 'none', width: '35px', height: '35px', borderRadius: '10px', cursor: 'pointer' },
    deleteBtn: { background: '#FEE2E2', color: '#EF4444', border: 'none', width: '35px', height: '35px', borderRadius: '10px', cursor: 'pointer' }
};

export default AdminFAQSection;